import { Injectable } from '@angular/core';
import {
  ADD_OFFER,
  ADD_QUANTITY,
  DELETE_OFFER_MUTATION,
  GET_ALL_OFFERS,
  GET_OFFER_BY_ID,
  UP_OFFER,
} from '@queries/product-offers-graphql';
import { Apollo } from 'apollo-angular';
import { WatchQueryFetchPolicy } from '@apollo/client/core';
import { cloneDeep, omit } from 'lodash';
import { toBulkPrice } from './offer-bulk';
import { toRequestedVolume } from './offer-containers';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { Offer } from './offer';

@Injectable({
  providedIn: 'root',
})
export class OffersService {
  private _item$: BehaviorSubject<Offer | null> = new BehaviorSubject(null);
  private _items$: BehaviorSubject<Offer[] | null> = new BehaviorSubject(null);

  private _item: Offer | null;
  private _items: Offer[] | null;

  constructor(private apollo: Apollo) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get offer$(): Observable<Offer> {
    return this._item$.asObservable();
  }

  get offers$(): Observable<Offer[]> {
    return this._items$.asObservable();
  }

  paginationTotal = 0;
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  getList(params: {
    where?: any;
    orderBy?: any;
    skip?: number;
    take?: number;
  }, fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'): Observable<Offer[]> {
    return (
      this.apollo
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .watchQuery<{ ProductOfferFindAll: { data: Offer[]; total?: number } }>(
          {
            query: GET_ALL_OFFERS,
            variables: {
              where: params.where,
              orderBy: params.orderBy,
              skip: params.skip,
              take: params.take,
            },
            fetchPolicy,
          },
        )
        .valueChanges.pipe(
          switchMap(({ data }) => {
            const freightTypes = data.ProductOfferFindAll.data;
            this.paginationTotal = data.ProductOfferFindAll.total;
            this._items = freightTypes;
            this._items$.next(this._items);
            return of(this._items);
          }),
        )
    );
  }

  getById(id: number): Observable<Offer> {
    if (!id) {
      this._item = [];
      this._item$.next(this._item);
      return of(this._item);
    }
    return this.apollo
      .watchQuery<any>({
        query: GET_OFFER_BY_ID,
        variables: {
          where: {
            id,
          },
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          const department = data.ProductOfferFindOne;
          this._item = department;
          this._item$.next(this._item);
          return of(this._item);
        }),
      );
  }

  search(
    query: any,
    opts: { orderBy?: any; skip?: number; take?: number } = {},
  ): Observable<Offer[]> {
    console.log(
      '🚀 ~ file: offers.service.ts:101 ~ OffersService ~ search ~ query',
      query,
    );

    // Clone the contacts
    let _items = cloneDeep(this._items);
    let where: any = {};

    if (query) {
      // Free text: matches the offer reference OR the species name (the other fields are ANDed).
      if (query.term) {
        where.OR = [
          { number: { contains: query.term, mode: 'insensitive' } },
          { specy: { is: { name: { contains: query.term, mode: 'insensitive' } } } },
        ];
      }

      if (query.supplier) {
        where.supplier = {
          "is": {
            "codigo": {
              "contains": query.supplier,
              "mode": "insensitive"
            }
          }
        }
      }

      if (query.ref) {
        where.number = {
          "contains": query.ref,
          "mode": "insensitive"
        }
      }

      if (query.specie) {
        where.specy = {
          "is": {
            "name": {
              "contains": query.specie,
              "mode": "insensitive"
            }
          }
        }
      }

      if (query.sawn) {
        where.sawnType = {
          "is": {
            "name": {
              "contains": query.sawn,
              "mode": "insensitive"
            }
          }
        }
      }
      if (query.thickness) {
        where.thick = {
          "contains": query.thickness,
          "mode": "insensitive"
        }
      }
      if (query.width) {
        where.width = {
          "contains": query.width,
          "mode": "insensitive"
        }
      }
      if (query.dry) {
        where.dryType = {
          "is": {
            "name": {
              "contains": query.dry,
              "mode": "insensitive"
            }
          }

        }
      }
      if (query.incon) {
        where.InconTerm = {
          "is": {
            "name": {
              "contains": query.incon,
              "mode": "insensitive"
            }
          }

        }
      }

      if (query.via) {
        where.via = {
          "equals": query.via?.toUpperCase(),
        }
      }

      if (query.type) {
        where.qualityType = {
          "is": {
            "name": {
              "contains": query.type,
              "mode": "insensitive"
            }
          }

        }
      }

      if (query.shp) {
        where.shipment = {
          "is": {
            "name": {
              "contains": query.shp,
              "mode": "insensitive"
            }
          }

        }
      }

      if (query.length) {
        where.length = {
          "contains": query.length,
          "mode": "insensitive"
        }
      }

    }

    // Filters, ordering and page travel together; default: newest date first, first page.
    return this.getList({
      where,
      skip: opts.skip ?? 0,
      take: opts.take ?? 100,
      orderBy: opts.orderBy ?? { date: 'desc' },
    })

    // // If the query exists...
    // if (query) {
    //   if (query.supplier) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.supplier &&
    //         item?.supplier?.externalCode
    //           .toLowerCase()
    //           .includes(query?.supplier?.toLowerCase()),
    //     );
    //   }

    //   if (query.ref) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.number &&
    //         item.number.toLowerCase().includes(query.ref?.toLowerCase()),
    //     );
    //   }

    //   if (query.specie) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.specy &&
    //         item?.specy?.name
    //           .toLowerCase()
    //           .includes(query.specie?.toLowerCase()),
    //     );
    //   }

    //   if (query.port) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.originPort &&
    //         item?.originPort?.name
    //           .toLowerCase()
    //           .includes(query.port?.toLowerCase()),
    //     );
    //   }

    //   if (query.sawn) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.sawnType &&
    //         item?.sawnType?.name
    //           .toLowerCase()
    //           .includes(query.sawn?.toLowerCase()),
    //     );
    //   }

    //   if (query.thickness) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.thick &&
    //         item?.thick.toLowerCase().includes(query.thickness?.toLowerCase()),
    //     );
    //   }
    //   if (query.width) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.width &&
    //         item?.width.toLowerCase().includes(query.width?.toLowerCase()),
    //     );
    //   }
    //   if (query.dry) {
    //     // Filter the _items
    //     _items = _items.filter(
    //       (item: Offer) =>
    //         item.dryType &&
    //         item?.dryType.name.toLowerCase().includes(query.dry?.toLowerCase()),
    //     );
    //   }
    // }

    // // Sort the _items by the name field by default
    // _items.sort((a, b) => a.id.localeCompare(b.id));

    // // Return the response
    // this._items$.next(_items);
    // return of(_items);
  }

  /**
   * CRUD
   */
  generateOfferQuantity(record: Offer, callback): Observable<any> {
    return this.apollo
      .mutate<any>({
        mutation: ADD_QUANTITY,
        variables: {
          data: {
            quantityType: { connect: { id: +record.quantityType } },
            quantity: +record.quantity,
            calculatedQuantity: 0,
          },
        },
      })
      .pipe(switchMap(({ data }) => callback(record, data.QuantityCreate)));
  }

  create(record: Offer): Observable<Offer[]> {
    // Container offers have no Quantity of their own: the backend aggregates the
    // volume from the container lines, so there is nothing to create first.
    if (record.usesContainers) {
      return this.addOffer(record, null);
    }

    /**
     * 1. tenho de criar a quantidade antes de enviar
     */
    return this.generateOfferQuantity(record, this.addOffer.bind(this));
  }

  /** Container lines of the form as the nested Prisma input of ProductOfferContainers.create. */
  private toContainerCreateInput(
    containers: { quantityTypeId: number | string; count: number | string }[]
  ) {
    return (containers ?? []).map((container) => ({
      quantityType: { connect: { id: +container.quantityTypeId } },
      count: +container.count,
    }));
  }

  addOffer(record: Offer, quantity: any): Observable<Offer[]> {
    return this.apollo
      .mutate<any>({
        mutation: ADD_OFFER,
        variables: {
          data: {
            // eslint-disable-next-line max-len
            ...omit(record, [
              'id',
              'countryId',
              'numberOf',
              'supplierType',
              'isCorrupted',
              'qualityTypeId',
              'quantity',
              'quantityType',
              'price',
              'priceId',
              'priceIsMainCurrency',
              'priceCurrencyId',
              'shipmentId',
              'inconTermId',
              'productStateId',
              'supplierId',
              'specyId',
              'originPortId',
              'dryTypeId',
              'sawnType',
              'sawnTypeId',
              'usesContainers',
              'containers',
              'requestedVolumeM3',
              'requestedContainerTypeId',
              'bulkPrice',
            ]),
            // Price per m3 in bulk: blank is null (the offer is not sold in bulk), never 0
            bulkPrice: toBulkPrice(record.bulkPrice),
            number: record.numberOf,
            supplierType: 'PRODUTOR' /*record.supplierType*/,
            supplier: { connect: { id: +record.supplierId?.id } },
            specy: { connect: { id: +record.specyId } },
            originPort: { connect: { id: +record.originPortId?.id } },
            dryType: { connect: { id: +record.dryTypeId } },
            sawnType: { connect: { id: +record.sawnTypeId } },
            qualityType: { connect: { id: +record.qualityTypeId } },
            productOfferState: { connect: { id: +record.productStateId } },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            InconTerm: { connect: { id: +record.inconTermId } },
            shipment: { connect: { id: +record.shipmentId } },
            ...(record.usesContainers
              ? {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  ProductOfferContainers: {
                    create: this.toContainerCreateInput(record.containers),
                  },
                  // D19: a blank form value is null, never 0
                  requestedVolumeM3: toRequestedVolume(record.requestedVolumeM3),
                }
              : { quantity: { connect: { id: +quantity.id } } }),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Pricings: {
              createMany: {
                data: [
                  {
                    value: +record.price,
                    currencyId: +record.priceCurrencyId,
                    isMainCurrency: record.priceIsMainCurrency,
                  },
                ],
                skipDuplicates: true,
              },
            },
          },
        },
      })
      .pipe(
        switchMap(({ data }) =>
          this.getList({ skip: 0, take: 100, orderBy: { id: 'desc' } }),
        ),
      );
  }

  update(record: Offer) {
    return this.apollo
      .mutate<any>({
        mutation: UP_OFFER,
        variables: {
          data: {
            // eslint-disable-next-line max-len
            //...omit(record, ['id', 'countryId', 'numberOf', 'supplierType', 'isCorrupted', 'qualityTypeId', 'quantity', 'quantityType', 'price', 'priceIsMainCurrency', 'priceCurrencyId', 'shipmentId', 'inconTermId', 'productStateId', 'supplierId', 'specyId', 'originPortId', 'dryTypeId', 'sawnType', 'sawnTypeId']),
            number: { set: record.numberOf },
            supplierType: { set: 'PRODUTOR' /*record.supplierType*/ },
            obs: { set: record.obs },
            date: { set: record.date },
            fsc: { set: record.fsc },
            thick: { set: record.thick },
            width: { set: record.width },
            length: { set: record.length },
            via: { set: record.via },
            profitMargin: { set: +record.profitMargin },
            bulkPrice: { set: toBulkPrice(record.bulkPrice) },
            supplier: { connect: { id: +record.supplierId?.id } },
            specy: { connect: { id: +record.specyId } },
            originPort: { connect: { id: +record.originPortId?.id } },
            dryType: { connect: { id: +record.dryTypeId } },
            sawnType: { connect: { id: +record.sawnTypeId } },
            qualityType: { connect: { id: +record.qualityTypeId } },
            productOfferState: { connect: { id: +record.productStateId } },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            InconTerm: { connect: { id: +record.inconTermId } },
            shipment: { connect: { id: +record.shipmentId } },

            ...(record.usesContainers
              ? {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  ProductOfferContainers: {
                    deleteMany: {},
                    create: this.toContainerCreateInput(record.containers),
                  },
                  requestedVolumeM3: { set: toRequestedVolume(record.requestedVolumeM3) },
                }
              : {
                  // D19: switching from FCL to a non container method must not leave a stale requested volume
                  requestedVolumeM3: { set: null },
                  quantity: {
                    update: {
                      quantity: {
                        set: +record.quantity,
                      },
                      quantityType: {
                        connect: {
                          id: +record.quantityType,
                        },
                      },
                    },
                  },
                }),

            // eslint-disable-next-line @typescript-eslint/naming-convention
            Pricings: {
              update: {
                where: {
                  id: +record.priceId,
                },
                data: {
                  value: {
                    set: +record.price,
                  },
                  currency: {
                    connect: {
                      id: +record.priceCurrencyId,
                    },
                  },
                },
              },
            },
          },
          where: {
            id: +record.id,
          },
        },
      })
      .pipe(
        switchMap(({ data }) =>
          this.getList({ skip: 0, take: 100, orderBy: { id: 'desc' } }),
        ),
      );
  }

  delete(id: number): Observable<any> {
    return this.apollo
      .mutate<any>({
        mutation: DELETE_OFFER_MUTATION,
        variables: {
          where: {
            id,
          },
        },
      })
      .pipe(
        switchMap(({ data }) =>
          this.getList({ skip: 0, take: 100, orderBy: { id: 'desc' } }),
        ),
      );
  }
}
